<script setup>
import { useUserStore } from "@/store/user-store";
import { Chip, Column, DataTable, Dialog, Rating } from "primevue";
import { onMounted, ref, watch, watchEffect, computed } from "vue";
import AnLoader from "../common/AnLoader.vue";
import AnButton from "../common/AnButton.vue";
import {
  chartColors,
  gridHighlightPlugin,
  lineChartOptions,
  pieChartOptions,
} from "@/services/constant-services";
import Chart from "primevue/chart";
import { convertToInr } from "@/services/common-services";
import NewsSection from "@/sections/dashboard/NewsSection.vue";

const props = defineProps({
  isStockDialogOpen: {
    type: Boolean,
    default: false,
  },
  stock: {
    type: Object,
    default: {},
  },
});

const emit = defineEmits(["update:isStockDialogOpen"]);

const dialogVisible = ref(props.isStockDialogOpen);
const dataLoading = ref(false);
const stockData = ref(null);
const quarterlyPerformance = ref(null);
const news = ref(null);
const profitLoss = ref(null);
const generalData = ref(null);
const balanceSheet = ref(null);
const cashFlows = ref(null);
const shareholdingPattern = ref(null);
const store = useUserStore();
const isExpanded = ref(false);
const maxLength = 300;

const truncatedText = computed(() => {
  if (!stockData) return "";
  const text = stockData.value.aboutCompany || "";
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
});

const showToggle = computed(() => {
  const text = stockData.value.aboutCompany || "";
  return text.length > maxLength;
});

const loadStockData = async () => {
  dataLoading.value = true;
  try {
    const response = await store.getStocksData(props.stock.stockName);
    stockData.value = response;
  } catch (error) {
    store.fireAlert("Error", error.message);
  } finally {
    dataLoading.value = false;
  }
};

watchEffect(() => {
  if (!stockData.value) return;

  generalData.value = [
    {
      key: "Market cap",
      value: "₹ " + stockData.value.mainSection.marketCap + " Cr.",
    },
    {
      key: "Current Price",
      value: "₹ " + stockData.value.mainSection.currentPrice,
    },
    {
      key: "Stock P/E",
      value: stockData.value.mainSection.priceEarningsRatio,
    },
    {
      key: "ROCE",
      value: stockData.value.mainSection.ROCE + " %",
      //Add tooltip
      tooltip: "",
    },
    {
      key: "ROE",
      value: stockData.value.mainSection.ROE + " %",
    },
    {
      key: "52 Week High/Low",
      value:
        "₹ " +
        stockData.value.mainSection.Wk52High +
        " / " +
        stockData.value.mainSection.Wk52Low,
      //Add tooltip
      tooltip: "",
    },
  ];

  const quarterData =
    stockData.value && stockData.value.quarterPerformanceData
      ? stockData.value.quarterPerformanceData
      : {};
  const qpMonths = Object.keys(quarterData);
  if (qpMonths.length === 0) {
    quarterlyPerformance.value = { columns: [], data: [] };
  } else {
    const firstMonth = qpMonths[0];
    const qpMetrics = Object.keys(quarterData[firstMonth] || {});
    const qpRows = qpMetrics.map((metric) => {
      const row = { metric };
      qpMonths.forEach((month) => {
        row[month] = quarterData[month]
          ? quarterData[month][metric]
          : undefined;
      });
      return row;
    });
    const qpFilteredRows = qpRows.filter((row) =>
      qpMonths.some((month) => {
        const v = row[month];
        return v !== undefined && v !== null && Number(v) !== 0;
      })
    );
    quarterlyPerformance.value = {
      columns: qpMonths,
      data: qpFilteredRows,
    };
  }

  const plMonths = Object.keys(stockData.value.profitAndLossData || {});
  const plMetrics = Object.keys(stockData.value.profitAndLossData[plMonths[0]]);

  const plRows = plMetrics.map((metric) => {
    const row = { metric };
    plMonths.forEach((month) => {
      row[month] = stockData.value.profitAndLossData[month][metric];
    });
    return row;
  });

  profitLoss.value = {
    columns: plMonths,
    data: plRows,
  };

  const bsMonths = Object.keys(stockData.value.balanceSheetData || {});
  const bsMetrics = Object.keys(stockData.value.balanceSheetData[bsMonths[0]]);

  const bsRows = bsMetrics.map((metric) => {
    const row = { metric };
    bsMonths.forEach((month) => {
      row[month] = stockData.value.balanceSheetData[month][metric];
    });
    return row;
  });

  balanceSheet.value = {
    columns: bsMonths,
    data: bsRows,
  };

  const cfMonths = Object.keys(stockData.value.cashFlowData || {});
  const cfMetrics = Object.keys(stockData.value.cashFlowData[cfMonths[0]]);

  const cfRows = cfMetrics.map((metric) => {
    const row = { metric };
    cfMonths.forEach((month) => {
      row[month] = stockData.value.cashFlowData[month][metric];
    });
    return row;
  });

  cashFlows.value = {
    columns: cfMonths,
    data: cfRows,
  };

  const spLabels = Object.keys(stockData.value.shareholdingPattern || {});
  const spValues = spLabels.map((label) =>
    stockData.value.shareholdingPattern[label].split("%")[0].trim()
  );
  shareholdingPattern.value = {
    labels: spLabels,
    datasets: [
      {
        data: spValues,
        backgroundColor: chartColors.slice(0, spLabels.length),
      },
    ],
  };

  news.value =
    stockData.value.news.map((item) => ({
      newsHeadline: item.title,
      newsURL: item.url,
    })) || [];
});

watch(
  () => props.isStockDialogOpen,
  (newVal) => {
    dialogVisible.value = newVal;
  }
);

watch(dialogVisible, (newVal) => {
  emit("update:isStockDialogOpen", newVal);
});

onMounted(() => {
  loadStockData();
});
</script>

<template>
  <Dialog
    modal
    v-model:visible="dialogVisible"
    :closable="false"
    :dismissable-mask="!dataLoading"
  >
    <template #header>
      <div
        class="d-flex align-items-center position-relative w-100 p-20 dialog-header"
      >
        <span class="text-center text-primary w-100 font-20 font-weight-700">{{
          stock.stockName
        }}</span>
        <i
          @click="emit('update:isStockDialogOpen', false)"
          class="pi pi-times bg-error text-white p-5 br-5 position-absolute r-0 mr-10 cursor-pointer"
        />
      </div>
    </template>
    <div v-if="dataLoading" class="position-relative loader-container">
      <AnLoader text="Stock data loading..." />
    </div>
    <div v-if="stockData" class="ph-30 pb-30">
      <div class="d-grid input-grid gap-15 font-20 mt-30">
        <div class="d-flex gap-10" v-for="item in generalData">
          <span class="font-weight-600">{{ item.key }}:</span>
          <span class="font-weight-400">
            {{ item.value }}
            <i
              v-if="item.tooltip && item.key === 'Expense Ratio'"
              v-tooltip.right="{
                value: item.tooltip,
                class: 'left-arrow short-width',
              }"
              class="pi pi-info-circle font-15"
            />
            <i
              v-else-if="item.tooltip && item.key === 'Benchmark TRI 1 Year'"
              v-tooltip.left="{
                value: item.tooltip,
                class: 'right-arrow short-width',
              }"
              class="pi pi-info-circle font-15"
            />
          </span>
        </div>
      </div>
      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">About</span>
        <span class="h-divider mt-5" />
        <span class="mt-20">
          {{ isExpanded ? stockData?.aboutCompany : truncatedText }}
          <button
            v-if="showToggle"
            class="read-more-btn"
            @click="isExpanded = !isExpanded"
          >
            {{ isExpanded ? "Read less" : "Read more" }}
          </button>
        </span>
      </div>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">Quarterly Results</span>
        <span class="h-divider mt-5" />
      </div>
      <DataTable
        :value="quarterlyPerformance.data"
        class="mt-20"
        :pt="{
          headerRow: { class: 'font-weight-500' },
        }"
      >
        <Column
          field="metric"
          header=""
          frozen
          class="font-weight-600 text-primary"
        />

        <Column
          v-for="month in quarterlyPerformance.columns"
          :key="month"
          :field="month"
          :header="month"
          :body="
            (data) =>
              data[month]?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
          "
        />
      </DataTable>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">Profit & Loss</span>
        <span class="h-divider mt-5" />
      </div>
      <DataTable
        :value="profitLoss.data"
        class="mt-20"
        :pt="{
          headerRow: { class: 'font-weight-500' },
        }"
      >
        <Column
          field="metric"
          header=""
          frozen
          class="font-weight-600 text-primary"
        />

        <Column
          v-for="month in profitLoss.columns"
          :key="month"
          :field="month"
          :header="month"
          :body="
            (data) =>
              data[month]?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
          "
        />
      </DataTable>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">Balance sheet</span>
        <span class="h-divider mt-5" />
      </div>
      <DataTable
        :value="balanceSheet.data"
        class="mt-20"
        :pt="{
          headerRow: { class: 'font-weight-500' },
        }"
      >
        <Column
          field="metric"
          header=""
          frozen
          class="font-weight-600 text-primary"
        />

        <Column
          v-for="month in balanceSheet.columns"
          :key="month"
          :field="month"
          :header="month"
          :body="
            (data) =>
              data[month]?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
          "
        />
      </DataTable>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">Cash Flows</span>
        <span class="h-divider mt-5" />
      </div>
      <DataTable
        :value="cashFlows.data"
        class="mt-20"
        :pt="{
          headerRow: { class: 'font-weight-500' },
        }"
      >
        <Column
          field="metric"
          header=""
          frozen
          class="font-weight-600 text-primary"
        />

        <Column
          v-for="month in cashFlows.columns"
          :key="month"
          :field="month"
          :header="month"
          :body="
            (data) =>
              data[month]?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
          "
        />
      </DataTable>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-weight-500 bg-primary text-center p-10 text-white">
          - - - - - Shareholding Pattern - - - - -</span
        >
        <div class="d-flex bg-light pv-20">
          <Chart
            type="pie"
            :data="shareholdingPattern"
            :options="pieChartOptions"
            class="d-flex flex-1 justify-center align-items-center"
            :height="170"
            :width="170"
          />
          <div class="flex-1 d-flex gap-10 h-max flex-wrap">
            <Chip
              v-for="(label, index) in shareholdingPattern.labels"
              :key="index"
              class="bg-primary p-10 br-10 font-weight-500"
            >
              <span
                class="p-5 br-round mr-5"
                :style="{
                  backgroundColor:
                    shareholdingPattern.datasets[0].backgroundColor?.[index] ||
                    chartColors[index % chartColors.length],
                }"
              ></span>
              <span class="text-white"
                >{{ label }}:
                {{ shareholdingPattern.datasets[0].data[index] }}%</span
              >
            </Chip>
          </div>
        </div>
      </div>
      <NewsSection :news="news" class="mt-30" />
    </div>
    <template #footer>
      <div class="d-flex justify-center w-100 p-10">
        <AnButton
          label="Back"
          variant="primary"
          :is-disabled="dataLoading"
          @click="emit('update:isStockDialogOpen', false)"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.loader-container {
  height: 400px;
}

.dialog-header {
  border-bottom: 2px solid;
  border-bottom-color: var(--an-primary);
}

.input-grid {
  grid-template-columns: repeat(3, 1fr);
}

.legend {
  border: 1px solid var(--an-gray1);
}

.read-more-btn {
  background: none;
  border: none;
  color: var(--an-primary);
  font-weight: 600;
  cursor: pointer;
  margin-left: 5px;
  padding: 0;
}
.read-more-btn:hover {
  text-decoration: underline;
}
</style>
